import type { Product, Discount } from '@/payload-types'

import { Link } from '@/i18n/routing'
import React from 'react'
import clsx from 'clsx'
import { Media } from '@/components/Media'
import { Price } from '@/components/Price'
import {
  ProductBanner,
  VariantBanner,
  BulkDiscountBanner,
  DiscountRibbon,
} from '@/components/product/ProductBanners'
import { getProductDiscountInfo, calculateDiscountedPrice } from '@/lib/productDiscounts'
import styles from '@/styles/liquid-glass.module.css'
import { ShoppingCart } from 'lucide-react'
import { WishlistButton } from '@/components/WishlistButton'

type Props = {
  product: Partial<Product>
  activeDiscounts?: Discount[]
  isLoggedIn?: boolean
  locale?: string
}

export const ProductGridItem: React.FC<Props> = ({ product, activeDiscounts, isLoggedIn, locale }) => {
  const { gallery, basePrice, title, variants } = product

  // Calculate price from basePrice or variant prices
  const price = basePrice
  const hasVariants = Array.isArray(variants) && variants.length > 0

  let lowestAmount = 0
  let highestAmount = 0

  if (hasVariants) {
    const allPrices = variants.flatMap(
      (v) =>
        v.values?.map((val) => val.price).filter((p): p is number => typeof p === 'number') || [],
    )
    if (allPrices.length > 0) {
      lowestAmount = Math.min(...allPrices)
      highestAmount = Math.max(...allPrices)
    }
  }

  // Get discount information
  const discountInfo = getProductDiscountInfo(product, activeDiscounts)

  const metaImage =
    product.meta?.image && typeof product.meta.image !== 'string' ? product.meta.image : undefined

  const image = metaImage || false

  return (
    <Link
      data-testid="product-grid-item"
      data-product-id={product.id}
      className="relative flex flex-col h-full w-full group transition-all duration-300 ease-out hover:-translate-y-1.5"
      href={`/products/${product.slug}`}
    >
      <div
        className={clsx(
          'relative flex flex-col flex-1 overflow-hidden rounded-2xl',
          'bg-white/[0.07] dark:bg-white/[0.04]',
          'backdrop-blur-[18px] saturate-[1.35]',
          'ring-1 ring-white/[0.16]',
          'shadow-[0_3px_18px_rgba(0,0,0,0.22),inset_0_1px_0_rgba(255,255,255,0.28),inset_0_-1px_0_rgba(255,255,255,0.05)]',
          'group-hover:ring-white/[0.34]',
          'group-hover:shadow-[0_0_0_1.5px_rgba(255,255,255,0.10),0_18px_52px_rgba(0,0,0,0.32),inset_0_1.5px_0_rgba(255,255,255,0.55),inset_0_-1px_0_rgba(255,255,255,0.10)]',
          'transition-all duration-300',
        )}
      >
        <div
          className={clsx(styles.liquidGlassContainer, 'transition-all duration-300')}
          style={{
            padding: '0.75rem',
            background: 'transparent',
            backdropFilter: 'none',
            WebkitBackdropFilter: 'none',
            border: 'none',
            borderRadius: '0',
            boxShadow: 'none',
          }}
        >
          {image ? (
            <div className="relative aspect-square">
              <Media
                className={clsx(
                  'relative aspect-square object-cover rounded-xl bg-transparent',
                  styles.liquidGlassImage,
                )}
                height={80}
                imgClassName={clsx('h-full w-full object-cover rounded-xl', {
                  'transition duration-300 ease-in-out group-hover:scale-105': true,
                })}
                resource={image}
                width={80}
              />
              <WishlistButton
                productId={String(product.id)}
                variant="icon"
                isLoggedIn={isLoggedIn}
                loginHref={locale ? `/${locale}/login` : undefined}
              />
            </div>
          ) : (
            <div className="relative aspect-square rounded-xl bg-white/5 flex items-center justify-center">
              <div className="text-white/30 text-4xl">📦</div>
              <WishlistButton
                productId={String(product.id)}
                variant="icon"
                isLoggedIn={isLoggedIn}
                loginHref={locale ? `/${locale}/login` : undefined}
              />
            </div>
          )}

          <div className="absolute z-10 bottom-3 right-3 opacity-0 group-hover:opacity-100 transition-all duration-200 transform translate-y-2 group-hover:translate-y-0">
            <div
              className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-mono uppercase tracking-wider text-white bg-[var(--neon-orange)]/90 hover:bg-[var(--neon-orange)] shadow-lg backdrop-blur-sm transition-colors"
              aria-label="Quick view"
            >
              <ShoppingCart className="w-3.5 h-3.5" />
              <span>View</span>
            </div>
          </div>

          <ProductBanner>
            {discountInfo.hasVariants && <VariantBanner />}
            {discountInfo.hasBulkDiscount && <BulkDiscountBanner />}
          </ProductBanner>

          {discountInfo.hasManualDiscount && discountInfo.manualDiscountPercentage && (
            <DiscountRibbon discountPercentage={discountInfo.manualDiscountPercentage} />
          )}
        </div>

        <div
          className={clsx(
            'relative font-mono flex flex-col items-center text-center gap-1.5',
            'px-3 pt-3 pb-3.5',
            'border-t border-white/[0.10] group-hover:border-white/[0.20]',
            'bg-transparent transition-colors duration-300',
          )}
        >
          <div className="text-white font-semibold text-sm tracking-wide drop-shadow-[0_2px_4px_rgba(0,0,0,0.55)] group-hover:text-white transition-colors leading-snug px-1">
            {title}
          </div>

          {typeof price === 'number' && price > 0 ? (() => {
            // Calculate discounted price for manual discounts on non-variant products
            const hasDiscount = discountInfo.hasManualDiscount && discountInfo.manualDiscountPercentage
            const discountedPrice = hasDiscount
              ? calculateDiscountedPrice(price, discountInfo.manualDiscountPercentage!)
              : price

            return (
              <div className="text-emerald-400 font-bold text-base drop-shadow-[0_1px_3px_rgba(0,0,0,0.4)]">
                <Price
                  amount={discountedPrice}
                  originalAmount={hasDiscount ? price : undefined}
                />
              </div>
            )
          })() : hasVariants && lowestAmount > 0 ? (
            <div className="text-emerald-400 font-bold text-base drop-shadow-[0_1px_3px_rgba(0,0,0,0.4)]">
              {highestAmount > lowestAmount ? (
                <Price lowestAmount={lowestAmount} highestAmount={highestAmount} />
              ) : (
                <Price amount={lowestAmount} />
              )}
            </div>
          ) : (
            <div className="text-neutral-400 text-xs">Price not available</div>
          )}
        </div>
      </div>
    </Link>
  )
}
